// Angular imports
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// RxJs imports
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

// Service imports
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';

// Model imports
import { WorkFlowParametersDto } from '../_models/workflow-parameters-dto';
import { WorkflowNotesResultDto } from '../_models/workflow-notes-result-dto';
import { SaveWorkflowNotesParametersDto } from '../_models/save-workflow-notes-parameters-dto';
import { WorkflowHistoryResultDto } from '../_models/wfe-workflow-history-result-dto';
import { WFVendorHistoryParametersDto } from '../_models/wf-vendor-history-parameters-dto';
import { WFInvoiceHistoryResultDto } from '../_models/wf-invoice-history-result-dto';
import { WFInvoiceHistoryParametersDto } from '../_models/wf-invoice-history-parameters-dto';
import { SubCategoryByGlAccountParametersDto } from '../../cirabooks/shared-cirabooks-module/_models/common/subcategory-by-gl-account-parametere-dto';
import { GLSubcategorySelectorResultDto } from '../../cirabooks/shared-cirabooks-module/_models/common/gl-account/glsubcategory-selector-result-dto';
import { APBillReclassRawDto } from '../../cirabooks/shared-cirabooks-module/_models/accounts-payable/ap-bill/ap-bill-reclass-raw-dto';
import { APBillParameterDto } from '../../cirabooks/shared-cirabooks-module/_models/accounts-payable/ap-bill/ap-bill-parameter-dto';
import { APBillReclassEditorResultDto } from '../../cirabooks/shared-cirabooks-module/_models/accounts-payable/ap-bill/ap-bill-reclass-editor-result-dto';
import { WFPaymentHistoryResultDto } from '../_models/wf-payment-history-result-dto';
import { WorkflowDetailsResultDto } from '../_models/workflow-details-result-dto';
import { WFParametersDto } from '../_models/wf-parameter-dto';
import { BoardApprovalRulesResultDto } from '../_models/board-approval-rules-result-dto';
import { WorkflowEFTEditorResultDto } from '../_models/wf-vendor-eft-detail';
import { WFEEditorParameterDto } from '../_models/wfe-editor-parameter-dto';

// constant imports
import { CacheDuration } from './../../_constants/cache-duration';
import { URLConstants } from '../../_constants/url-constants';

@Injectable({
    providedIn: 'root'
})
export class WorkflowSharedService {
    private readonly _subjectShowDetails: Subject<any> = new Subject<any>();

    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    getReclassifyEditor(baseParams: APBillParameterDto): Observable<APBillReclassEditorResultDto> {
        const url = `${URLConstants.ccCBAPBillApiUrl}/reclassify${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(APBillReclassEditorResultDto, response)));
    }

    getSubCategories(params: SubCategoryByGlAccountParametersDto): Observable<GLSubcategorySelectorResultDto[]> {
        const url = `${URLConstants.ccCBAPBillApiUrl}/subcategories${params.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, params.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any[]) => plainToClass(GLSubcategorySelectorResultDto, response)));
    }

    getWorkflowReadOnlyNotes(baseParams: WorkFlowParametersDto): Observable<WorkflowNotesResultDto> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}wfnotes${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WorkflowNotesResultDto, response)));
    }

    saveWorkflowNewNotes(baseParams: SaveWorkflowNotesParametersDto): Observable<string> {
        return this.httpClient
            .post(`${URLConstants.ccWorkflowViewerBaseApiUrl}wfnotes`, classToPlain(baseParams))
            .pipe(map((responseData: any) => responseData.toString()));
    }

    downloadInvoice(baseParams: WorkFlowParametersDto): Observable<Blob> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}downloadinvoice${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);

        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    getBoardNotes(baseParam: WorkFlowParametersDto): Observable<WorkflowNotesResultDto> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}board/notes${baseParam.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParam.clearCache);

        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any) => plainToClass(WorkflowNotesResultDto, response)));
    }

    saveBoardNotes(baseParams: SaveWorkflowNotesParametersDto): Observable<string> {
        return this.httpClient
            .post(`${URLConstants.ccWorkflowViewerBaseApiUrl}board/notes`, classToPlain(baseParams))
            .pipe(map((responseData: any) => responseData.toString()));
    }

    getWorkflowHistory(baseParams: WorkFlowParametersDto): Observable<WorkflowHistoryResultDto[]> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}wfhistory${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any[]) => plainToClass(WorkflowHistoryResultDto, response)));
    }

    getWorkflowVendorHistory(
        baseParams: WFVendorHistoryParametersDto,
        clearCache = true
    ): Observable<WFInvoiceHistoryResultDto[]> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}vendor/history${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any[]) => plainToClass(WFInvoiceHistoryResultDto, response)));
    }

    getWorkflowInvoiceHistory(
        baseParams: WFInvoiceHistoryParametersDto,
        clearCache = true
    ): Observable<WFInvoiceHistoryResultDto[]> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}invoice/history${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any[]) => plainToClass(WFInvoiceHistoryResultDto, response)));
    }

    getWorkflowVendorAccountHistory(
        baseParams: WFVendorHistoryParametersDto,
        clearCache = true
    ): Observable<WFInvoiceHistoryResultDto[]> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}vendor/accounthistory${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any[]) => plainToClass(WFInvoiceHistoryResultDto, response)));
    }

    getWorkflowPaymentHistory(baseParams: WorkFlowParametersDto): Observable<WFPaymentHistoryResultDto[]> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}payment/history${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any[]) => plainToClass(WFPaymentHistoryResultDto, response)));
    }

    reclassifyBill(baseParam: APBillReclassRawDto): Observable<any> {
        const url = `${URLConstants.ccCBAPBillApiUrl}/reclassify`;
        return this.httpClient.post(url, classToPlain(baseParam)).pipe(map((responseData: any) => responseData));
    }

    getWorkflowDetail(baseParams: WorkFlowParametersDto): Observable<WorkflowDetailsResultDto> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}details${baseParams.getQueryString()}`;

        return this.httpClient.get(url).pipe(map((response: any) => plainToClass(WorkflowDetailsResultDto, response)));
    }

    getBoardApprovalRules(baseParams: WFParametersDto): Observable<BoardApprovalRulesResultDto> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}board-approval-rules${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any) => plainToClass(BoardApprovalRulesResultDto, response)));
    }

    getEFTEditor(baseParams: WFEEditorParameterDto): Observable<WorkflowEFTEditorResultDto> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}eft/editor${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(WorkflowEFTEditorResultDto, response)));
    }

    updateDetailsParams(value: any): void {
        this._subjectShowDetails.next(value);
    }

    getDetailsParams(): Observable<any> {
        return this._subjectShowDetails;
    }
}
